// Palette registry — the source-of-truth list of selectable UI color palettes.
//
// Ported from the Claude Design handoff
// (Ref/design/color_palette/.../palette-app.jsx). Each palette is plain data so
// it survives HMR and can be read by non-React modules. Colors are driven into
// the app as CSS custom properties (see tokens.js / paletteStore.js) — NOT via
// React state — so switching a palette repaints without re-rendering the graph
// hot path.
//
// Shape of a palette:
//   id, name, group          — identity + grouping in the picker
//   approach, notes          — copy shown in the Appearance panel
//   isLight                  — light-mode palette (flips text/bg intent)
//   surfaces: bg, bgGraph, panel, panelHi, line, lineHi, nodeBg, nodeBorder, gridImg
//   text:     fg, fg2, fg3
//   accent:   accent, accentSoft
//   wires:    { anim, audio, event, state, output }  (data-type-coded)
//   wireNeutral?, statusOnline?, danger?
//   headerStyle: 'tinted' | 'bar' | 'edge' | 'neutral' | 'glow' | 'solid-tile'
//   wireStyle:   'typed-soft' | 'typed-glow' | 'neutral' | 'single'
//   chrome:   font, radius, radiusSm, shadowCard, headSize, headLs, ease
//             (Studio contract extension — every palette declares these
//             explicitly; legacy entries spread LEGACY_CHROME so the extension
//             is behavior-preserving for them)

const STUDIO_FONT = "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, sans-serif"

// One mono stack for every palette — mono is mono; palettes are color schemes,
// not typography schemes.
const MONO_STACK = "ui-monospace, 'Cascadia Mono', Menlo, Consolas, monospace"

// The pre-Studio chrome, spread into every legacy palette — radii/shadows are
// what the app hardcoded before the Studio restyle, so legacy palettes keep
// their shape. Typography is the exception: fonts are GLOBAL, not per-palette.
// Every palette (legacy included) resolves --m-font to Hanken Grotesk and
// --m-mono to the shared mono stack — the shipped typeface must show up
// everywhere, whatever color scheme the user picked.
const LEGACY_CHROME = {
  font: STUDIO_FONT,
  mono: MONO_STACK,
  radius: '6px',
  radiusSm: '4px',
  shadowCard: '0 8px 24px rgba(0,0,0,0.5)',
  headSize: '10.5px',
  headLs: '0.12em',
  ease: '.12s',
  // Ink on an accent fill (primary/confirm buttons). Legacy accent buttons
  // have always drawn white-ish text on the accent color — keep that.
  accentInk: '#ffffff',
  // Legacy palettes carry no `stage` — tokens.js falls back to bgGraph, which
  // is exactly what the viewport Canvas read before --m-stage existed.
}

// The Studio pair (adopted from the gass_front design system) is the shipped
// default — new sessions boot into studio-light. Users with a persisted
// palette choice (scoped localStorage / server preference) keep it.
export const DEFAULT_PALETTE_ID = 'studio-light'

export const PALETTES = [
  // ============ STUDIO (design-system default) =========================
  {
    id: 'studio-light',
    name: 'Studio Light',
    approach: 'Warm paper-white · mint accent · Hanken Grotesk · soft cards',
    notes: 'The AvatarOS Studio design system, light theme. Warm off-white surfaces with true-white panels, a single mint-green accent, soft card shadows and larger radii. This is the shipped default look.',
    group: 'Studio',
    bg: '#f4f4f2', bgGraph: '#f7f7f4',
    panel: '#ffffff', panelHi: '#fbfbfa',
    line: '#e6e5e1', lineHi: '#d8d7d2',
    nodeBg: '#ffffff', nodeBorder: '#e6e5e1',
    fg: '#17181a', fg2: '#77766f', fg3: '#9a9993',
    accent: '#2aa87c', accentSoft: '#e7fbf2',
    gridImg: 'radial-gradient(#e1e0dc 1px, transparent 1px)',
    wires: { anim: '#3d97b5', audio: '#c07a37', event: '#c25555', state: '#679a44', output: '#b39a3a' },
    wireNeutral: 'rgba(23,24,26,0.35)',
    statusOnline: '#2aa87c',
    danger: '#c4564a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: true,
    font: STUDIO_FONT,
    mono: MONO_STACK,
    radius: '14px', radiusSm: '8px',
    shadowCard: '0 4px 16px rgba(0,0,0,.04)',
    headSize: '10.5px', headLs: '0.12em', ease: '.12s',
    // Ink-on-accent (gass mintink) + the always-dark 3D stage card
    // (gass .stu-stagecard: the render view stays dark in BOTH themes).
    accentInk: '#0b3d2c',
    stage: '#101114',
  },
  {
    id: 'studio-dark',
    name: 'Studio Dark',
    approach: 'Ink-dark · pale mint accent · Hanken Grotesk · soft cards',
    notes: 'The AvatarOS Studio design system, dark theme. Near-black ground with cool graphite panels and a pale mint accent — the designed dark twin of Studio Light.',
    group: 'Studio',
    bg: '#0d0e10', bgGraph: '#141519',
    panel: '#23262d', panelHi: '#17191e',
    line: '#33373f', lineHi: '#3d424b',
    nodeBg: '#23262d', nodeBorder: '#33373f',
    fg: '#eceef0', fg2: '#a6adb6', fg3: '#7a8089',
    accent: '#7ff0c4', accentSoft: 'rgba(127,240,196,.16)',
    gridImg: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
    wires: { anim: '#5fb8d4', audio: '#e89456', event: '#d96b6b', state: '#8ab85f', output: '#e8c95a' },
    wireNeutral: 'rgba(255,255,255,0.35)',
    statusOnline: '#7ff0c4',
    danger: '#c4564a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    font: STUDIO_FONT,
    mono: MONO_STACK,
    radius: '14px', radiusSm: '8px',
    shadowCard: '0 4px 16px rgba(0,0,0,.3)',
    headSize: '10.5px', headLs: '0.12em', ease: '.12s',
    accentInk: '#0b3d2c',
    stage: '#101114',
  },

  {
    id: 'myra-default',
    name: 'Myra (legacy default)',
    approach: 'Near-black studio look · typed wires · tinted headers',
    notes: 'The pre-Studio default: near-black panels, warm red-orange accent, and data-typed wires pulled back so the 3D viewport stays the brightest thing on screen. This is the baseline every other direction is compared against.',
    group: 'Legacy default',
    bg: '#1a1a1a', bgGraph: '#141414',
    panel: '#161616', panelHi: '#1f1f1f',
    line: 'rgba(255,255,255,0.08)', lineHi: 'rgba(255,255,255,0.14)',
    nodeBg: '#1c1c1c', nodeBorder: 'rgba(255,255,255,0.10)',
    fg: '#e6e6e6', fg2: '#aaaaaa', fg3: '#666666',
    accent: '#e8663c', accentSoft: 'rgba(232,102,60,0.18)',
    gridImg: 'radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)',
    wires: { anim: '#5fb8d4', audio: '#e89456', event: '#d96b6b', state: '#8ab85f', output: '#e8c95a' },
    statusOnline: '#7fcb6a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ MID-GREY · DCC LINEAGE =================================
  {
    id: 'compositor-stone',
    name: 'Compositor Stone',
    approach: 'Warm mid-grey · saturated typed wires · tinted headers',
    notes: 'Classic compositing-suite mid-grey — panels sit at ~38% lightness instead of the usual near-black. The UI is bright enough to work in a lit studio without squinting, and the 3D viewport pops against it rather than blending in. Wires keep more saturation here because the brighter background can absorb it.',
    group: 'Mid-grey · DCC lineage',
    bg: '#4a4642', bgGraph: '#54504b',
    panel: '#3e3a36', panelHi: '#4a4641',
    line: 'rgba(0,0,0,0.18)', lineHi: 'rgba(255,255,255,0.10)',
    nodeBg: '#3a3633', nodeBorder: 'rgba(0,0,0,0.35)',
    fg: '#f0ebe2', fg2: '#c4bcae', fg3: '#8e887d',
    accent: '#e8a657', accentSoft: 'rgba(232,166,87,0.22)',
    gridImg: 'radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)',
    wires: { anim: '#5fb8d4', audio: '#e89456', event: '#d96b6b', state: '#8ab85f', output: '#e8c95a' },
    statusOnline: '#7fcb6a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'channel-box-steel',
    name: 'Channel Box Steel',
    approach: 'Cool mid-grey · pale-blue selection · neutral wires + ports',
    notes: 'Cool steel panels in the lineage of attribute-editor UIs — light enough that white text reads as warm against it. Single pale-blue accent for selection and active rows. Wires are monochrome; only the port dots carry type. A very honest, instrument-like look.',
    group: 'Mid-grey · DCC lineage',
    bg: '#52575e', bgGraph: '#5b6068',
    panel: '#454a51', panelHi: '#525860',
    line: 'rgba(0,0,0,0.18)', lineHi: 'rgba(255,255,255,0.12)',
    nodeBg: '#3e434a', nodeBorder: 'rgba(0,0,0,0.32)',
    fg: '#eef2f7', fg2: '#c2c8d1', fg3: '#8b919b',
    accent: '#9fc7e8', accentSoft: 'rgba(159,199,232,0.22)',
    gridImg: 'radial-gradient(rgba(0,0,0,0.10) 1px, transparent 1px)',
    wires: { anim: '#9fc7e8', audio: '#e8b888', event: '#e89898', state: '#a8d49a', output: '#e8d49a' },
    wireNeutral: 'rgba(255,255,255,0.45)',
    statusOnline: '#7fdba0',
    headerStyle: 'neutral', wireStyle: 'neutral',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'dag-tiles',
    name: 'DAG Tiles',
    approach: 'Mid-grey canvas · solid-color node tiles · thin neutral wires',
    notes: 'The most distinctive direction. Nodes are NOT cards-with-headers — the whole node is a saturated colored tile. Category is encoded by the tile color itself: blue for anim, magenta for audio, salmon for events, green for state, yellow for output. Wires are thin and pale because the nodes carry all the color load. Chunky, legible, instantly readable as "node graph".',
    group: 'Mid-grey · DCC lineage',
    bg: '#4d4a47', bgGraph: '#555250',
    panel: '#3e3b39', panelHi: '#4a4744',
    line: 'rgba(0,0,0,0.22)', lineHi: 'rgba(255,255,255,0.10)',
    nodeBg: '#4f7da8', nodeBorder: 'rgba(0,0,0,0.4)',
    fg: '#f1ede5', fg2: '#c8c1b3', fg3: '#8e887d',
    accent: '#f0b95a', accentSoft: 'rgba(240,185,90,0.22)',
    gridImg: 'radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)',
    wires: { anim: '#4f7da8', audio: '#a96aa1', event: '#c66a5f', state: '#6f9b58', output: '#c9a14a' },
    wireNeutral: 'rgba(255,255,255,0.55)',
    statusOnline: '#86d06a',
    headerStyle: 'solid-tile', wireStyle: 'neutral',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'render-suite',
    name: 'Render Suite',
    approach: 'Medium-dark grey · vibrant orange single accent · solid headers',
    notes: 'Bright vibrant orange against medium-dark neutral grey — the look of a render-manager or asset browser. Headers are solid colored bars but pulled from a curated bright palette (orange, teal, magenta, lime). High signal, slightly retro, never apologetic about being a tool.',
    group: 'Mid-grey · DCC lineage',
    bg: '#3a3a3c', bgGraph: '#424244',
    panel: '#2f2f31', panelHi: '#3a3a3c',
    line: 'rgba(0,0,0,0.2)', lineHi: 'rgba(255,255,255,0.10)',
    nodeBg: '#363638', nodeBorder: 'rgba(0,0,0,0.35)',
    fg: '#f0eee9', fg2: '#bdbab4', fg3: '#86837d',
    accent: '#f08a3c', accentSoft: 'rgba(240,138,60,0.22)',
    gridImg: 'radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)',
    wires: { anim: '#3cb4b8', audio: '#f08a3c', event: '#e85c8a', state: '#8ec146', output: '#f0c043' },
    statusOnline: '#8ec146',
    headerStyle: 'bar', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ COOL & CALM ============================================
  {
    id: 'slate-workshop',
    name: 'Slate Workshop',
    approach: 'Cool neutral · pastel-typed wires · tinted node headers',
    notes: 'Cool slate base like a compositing or grading suite. Wires are typed by data but pulled down to ~45% saturation so they sit BEHIND the content. Headers carry a soft color wash so the category is readable at a glance without screaming.',
    group: 'Cool & Calm',
    bg: '#171a1f', bgGraph: '#1b1f25',
    panel: '#212630', panelHi: '#2a3140',
    line: 'rgba(255,255,255,0.06)', lineHi: 'rgba(255,255,255,0.12)',
    nodeBg: '#222831', nodeBorder: 'rgba(255,255,255,0.07)',
    fg: '#e6eaf2', fg2: '#9aa3b3', fg3: '#5f6877',
    accent: '#8fb6d9', accentSoft: 'rgba(143,182,217,0.18)',
    gridImg: 'radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)',
    wires: { anim: '#8fb6d9', audio: '#c4a3d1', event: '#d99a8f', state: '#a9c98f', output: '#e0c98f' },
    statusOnline: '#7fd09a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'cosmic-indigo',
    name: 'Cosmic Indigo',
    approach: 'Deep indigo · glowing wires · single cyan accent',
    notes: 'More dramatic — the canvas is near-black with a hint of indigo, and wires get a soft outer glow. Reads like a planetarium control board. Best when you want the graph itself to feel alive; risk is that it can feel less neutral against a 3D viewport.',
    group: 'Cool & Calm',
    bg: '#0c0e18', bgGraph: '#10121e',
    panel: '#171a28', panelHi: '#1f2336',
    line: 'rgba(255,255,255,0.06)', lineHi: 'rgba(255,255,255,0.14)',
    nodeBg: '#1a1e30', nodeBorder: 'rgba(120,150,255,0.12)',
    fg: '#ecefff', fg2: '#9ea4c4', fg3: '#5d6280',
    accent: '#6ad8e8', accentSoft: 'rgba(106,216,232,0.18)',
    gridImg: 'radial-gradient(rgba(140,170,255,0.05) 1px, transparent 1px)',
    wires: { anim: '#6ad8e8', audio: '#9d8ef0', event: '#e8829b', state: '#8fe0a8', output: '#ecd87a' },
    statusOnline: '#8fe0a8',
    headerStyle: 'glow', wireStyle: 'typed-glow',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ WARM & FOCUSED ==========================================
  {
    id: 'studio-charcoal',
    name: 'Studio Charcoal',
    approach: 'Warm neutral · pastel-typed wires · tinted headers',
    notes: 'The "DCC default" done warm. Background lifts to a warm dark grey so the 3D viewport stays the brightest, most saturated thing on screen. Single warm amber accent. Wires typed but dialed back. This is the safest, most professional direction.',
    group: 'Warm & Focused',
    bg: '#1a1816', bgGraph: '#1f1c1a',
    panel: '#252220', panelHi: '#2d2a26',
    line: 'rgba(255,255,255,0.06)', lineHi: 'rgba(255,255,255,0.12)',
    nodeBg: '#262320', nodeBorder: 'rgba(255,255,255,0.07)',
    fg: '#ede8e0', fg2: '#a39c91', fg3: '#6a655d',
    accent: '#d4a574', accentSoft: 'rgba(212,165,116,0.18)',
    gridImg: 'radial-gradient(rgba(255,230,200,0.03) 1px, transparent 1px)',
    wires: { anim: '#9ec7c2', audio: '#d9a87c', event: '#c98583', state: '#a9c08b', output: '#e0c98f' },
    statusOnline: '#7fcb6a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'sepia-atelier',
    name: 'Sepia Atelier',
    approach: 'Warm muted · ochre accents · edge-stripe node headers',
    notes: 'Distinctive: the whole UI shifts into sepia/ochre territory. Nodes are neutral and category is signaled by a left edge stripe rather than a full header bar. Quiet, opinionated, photo-printroom feel. The most ownable direction.',
    group: 'Warm & Focused',
    bg: '#1f1b16', bgGraph: '#241f19',
    panel: '#2a241d', panelHi: '#332c23',
    line: 'rgba(255,220,180,0.06)', lineHi: 'rgba(255,220,180,0.13)',
    nodeBg: '#2b251e', nodeBorder: 'rgba(255,220,180,0.08)',
    fg: '#ece1cf', fg2: '#a8997f', fg3: '#6a6051',
    accent: '#c89758', accentSoft: 'rgba(200,151,88,0.18)',
    gridImg: 'radial-gradient(rgba(255,220,180,0.04) 1px, transparent 1px)',
    wires: { anim: '#c89758', audio: '#a08b6e', event: '#b67560', state: '#8c9968', output: '#d4b884' },
    statusOnline: '#7fcb6a',
    headerStyle: 'edge', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ NEUTRAL & VIEWPORT-FRIENDLY ============================
  {
    id: 'filmic-gray',
    name: 'Filmic 18% Gray',
    approach: 'True neutral · monochrome wires · color only at ports',
    notes: 'Inspired by color-grading suites: the UI is a perceptually-neutral mid-grey so it never biases your view of the 3D content. Wires are pale white; data type lives ONLY in the colored port dots at each endpoint. Maximally calm, maximally honest.',
    group: 'Neutral & Viewport-friendly',
    bg: '#2a2a2a', bgGraph: '#2f2f2f',
    panel: '#353535', panelHi: '#404040',
    line: 'rgba(255,255,255,0.07)', lineHi: 'rgba(255,255,255,0.14)',
    nodeBg: '#363636', nodeBorder: 'rgba(255,255,255,0.08)',
    fg: '#ebebeb', fg2: '#a0a0a0', fg3: '#6a6a6a',
    accent: '#e8a86c', accentSoft: 'rgba(232,168,108,0.18)',
    gridImg: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
    wires: { anim: '#7fbfbb', audio: '#d9a87c', event: '#c98583', state: '#a9c08b', output: '#e8a86c' },
    wireNeutral: 'rgba(255,255,255,0.32)',
    statusOnline: '#7fcb6a',
    headerStyle: 'neutral', wireStyle: 'neutral',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'carbon-amber',
    name: 'Carbon & Amber',
    approach: 'Near-black · single warm accent · neutral everything else',
    notes: 'Radical minimalism. ONE color — amber — for accents, selection, alpha sliders, and the active tab. Wires are pure white at low opacity. Node categories are signaled only by a small dot on the header. Feels like a precision instrument.',
    group: 'Neutral & Viewport-friendly',
    bg: '#0e0d0c', bgGraph: '#131211',
    panel: '#1a1917', panelHi: '#221f1c',
    line: 'rgba(255,240,220,0.05)', lineHi: 'rgba(255,240,220,0.12)',
    nodeBg: '#1b1917', nodeBorder: 'rgba(255,240,220,0.08)',
    fg: '#f1ece2', fg2: '#998f7e', fg3: '#5a5448',
    accent: '#e8a657', accentSoft: 'rgba(232,166,87,0.18)',
    gridImg: 'radial-gradient(rgba(255,240,220,0.04) 1px, transparent 1px)',
    wires: { anim: '#e8a657', audio: '#bda07c', event: '#d8825e', state: '#a89876', output: '#f0c990' },
    wireNeutral: 'rgba(255,240,220,0.28)',
    statusOnline: '#7fcb6a',
    headerStyle: 'neutral', wireStyle: 'single',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ BOLD & DISTINCT ========================================
  {
    id: 'pastel-studio',
    name: 'Pastel Studio',
    approach: 'Slate base · bold pastel categorical · solid header bars',
    notes: 'For when you WANT the graph to read like a colored map. Solid header bars in a curated low-saturation palette: lavender, peach, mint, sky, butter. Same palette is reused for asset tags. High legibility at the cost of being more "designed" looking.',
    group: 'Bold & Distinct',
    bg: '#17181c', bgGraph: '#1b1d22',
    panel: '#22252b', panelHi: '#2b2f37',
    line: 'rgba(255,255,255,0.06)', lineHi: 'rgba(255,255,255,0.13)',
    nodeBg: '#23262d', nodeBorder: 'rgba(255,255,255,0.08)',
    fg: '#ecedf2', fg2: '#a0a4b0', fg3: '#62667a',
    accent: '#c4b3e8', accentSoft: 'rgba(196,179,232,0.2)',
    gridImg: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
    wires: { anim: '#9ec5e8', audio: '#e8b59e', event: '#e89bb5', state: '#a8d9b1', output: '#e8d49a' },
    statusOnline: '#a8d9b1',
    headerStyle: 'bar', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },
  {
    id: 'forest-workshop',
    name: 'Forest Workshop',
    approach: 'Desaturated green base · birch accents · earthy categoricals',
    notes: 'A weirder, more memorable swing: the panels carry a desaturated forest-green tint, paired with a warm birch accent. Categorical colors come from nature — moss, clay, dusk, slate. Less generic than charcoal, still calm for long sessions.',
    group: 'Bold & Distinct',
    bg: '#161a17', bgGraph: '#1a1f1c',
    panel: '#1f2520', panelHi: '#272e28',
    line: 'rgba(230,240,220,0.06)', lineHi: 'rgba(230,240,220,0.12)',
    nodeBg: '#1f2521', nodeBorder: 'rgba(230,240,220,0.08)',
    fg: '#e6ebe1', fg2: '#9aa495', fg3: '#5e6859',
    accent: '#d8b87a', accentSoft: 'rgba(216,184,122,0.18)',
    gridImg: 'radial-gradient(rgba(230,240,220,0.035) 1px, transparent 1px)',
    wires: { anim: '#a8c094', audio: '#d8b87a', event: '#c69077', state: '#92b5b0', output: '#d4c89a' },
    statusOnline: '#92b5b0',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: false,
    ...LEGACY_CHROME,
  },

  // ============ LIGHT MODE =============================================
  {
    id: 'paper-atelier',
    name: 'Paper Atelier',
    approach: 'Warm cream · ink-on-paper · single accent',
    notes: 'For artists who hate dark mode. Warm cream background, deep ink text, single warm accent. Wires are thin and dark; nodes carry a soft drop shadow like physical cards. Lower contrast than typical light modes — meant to feel like a sketchbook, not a spreadsheet.',
    group: 'Light Mode',
    bg: '#f4f0e8', bgGraph: '#ece7dc',
    panel: '#ebe5d6', panelHi: '#e1dac8',
    line: 'rgba(40,30,20,0.08)', lineHi: 'rgba(40,30,20,0.18)',
    nodeBg: '#fbf8f1', nodeBorder: 'rgba(40,30,20,0.12)',
    fg: '#2a2620', fg2: '#6a655a', fg3: '#9a9486',
    accent: '#a8623c', accentSoft: 'rgba(168,98,60,0.16)',
    gridImg: 'radial-gradient(rgba(40,30,20,0.06) 1px, transparent 1px)',
    wires: { anim: '#3c6e8a', audio: '#a8623c', event: '#8a3c3c', state: '#4a7a4a', output: '#7a6a3c' },
    statusOnline: '#3a8a5a',
    headerStyle: 'tinted', wireStyle: 'typed-soft',
    isLight: true,
    ...LEGACY_CHROME,
    // Light paper reads wrong under the heavy dark drop — soften it, keep depth.
    shadowCard: '0 4px 14px rgba(40,30,20,0.18)',
  },
]

const _byId = new Map(PALETTES.map(p => [p.id, p]))

export function getPalette(id) {
  return _byId.get(id) || _byId.get(DEFAULT_PALETTE_ID)
}

export function paletteIds() {
  return PALETTES.map(p => p.id)
}

// Palettes grouped by `group`, preserving declaration order — drives the
// grouped card layout in the Appearance panel.
export function palettesByGroup() {
  const map = new Map()
  for (const p of PALETTES) {
    if (!map.has(p.group)) map.set(p.group, [])
    map.get(p.group).push(p)
  }
  return Array.from(map.entries())
}


